import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Modal } from 'antd';
import dayjs from 'dayjs';
import { ModalRowsSkeleton } from '@/components/profile';
import { ROUTES } from '@/constants';
import { useEmployee } from '@/hooks/queries/use-employee';
import { DATE_FORMATS } from '@/utils/formUtils';
import { getUserFullName } from '@/utils/utils';

const keyOf = (v) => (v && typeof v === 'object' ? v.key : v);
const fmt = (value) =>
  value ? dayjs(value).format(DATE_FORMATS.DISPLAY) : null;

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-2.5 border-b border-zinc-100 py-2 text-sm">
      <span className="w-[130px] shrink-0 truncate whitespace-nowrap text-[13px] text-zinc-500">
        {label}
      </span>
      <span className="min-w-0 flex-1 break-words font-semibold">
        {children || '—'}
      </span>
    </div>
  );
}

/**
 * Quick look at one employee (from the company profile's search, contract
 * list…): who they are and their contract, with a way on to the full profile.
 * Same look as CompanyPreviewModal.
 */
export default function EmployeePreviewModal({ employeeId, onClose }) {
  const { t } = useTranslation(['translations', 'forms']);
  const tp = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });
  const f = (key) => t(key, { ns: 'forms' });
  const open = !!employeeId;
  const { data: employee, isLoading } = useEmployee(employeeId);

  const gd = employee?.generalData || {};
  const es = employee?.employmentSettings || {};
  const name = getUserFullName(employee?.firstName, employee?.lastName);
  const initials =
    `${employee?.firstName?.[0] || ''}${employee?.lastName?.[0] || ''}`.toUpperCase();
  const type = keyOf(es.employmentType);
  const sector =
    typeof employee?.employeeSector === 'object'
      ? employee.employeeSector?.sectorName
      : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      closable={false}
      classNames={{ content: '!p-0 overflow-hidden !rounded-[18px]' }}
      destroyOnHidden
    >
      <div className="flex items-center justify-between gap-3 bg-accent px-5 py-4 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white font-extrabold text-accent">
            {initials}
          </div>
          <div>
            <div className="text-lg font-extrabold">{name}</div>
            <div className="text-[13px] opacity-90">
              {[gd.position, sector].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
        <button
          type="button"
          aria-label={tp('close')}
          onClick={onClose}
          className="h-8 w-8 cursor-pointer rounded-full border-none bg-white/20 text-white"
        >
          ×
        </button>
      </div>

      {isLoading || !employee ? (
        <ModalRowsSkeleton rows={8} />
      ) : (
        <div className="grid grid-cols-2 gap-x-6 px-5 pt-3">
          <Row label={f('personalNumber')}>{gd.personalNumber}</Row>
          <Row label={f('employmentType')}>
            {type === 'limited'
              ? tp('limited')
              : type === 'unlimited'
                ? tp('unlimited')
                : type}
          </Row>
          <Row label={f('email')}>{gd.email}</Row>
          <Row label={f('startDate')}>{fmt(es.startDate)}</Row>
          <Row label={f('phoneNumber')}>{gd.phoneNumber}</Row>
          <Row label={f('endDate')}>{fmt(es.endDate)}</Row>
          <Row label={f('city')}>{gd.city}</Row>
          <Row label={f('sector')}>{sector}</Row>
        </div>
      )}

      <div className="flex justify-end gap-2 px-5 pb-5 pt-4">
        <Button className="btn-default btn-default--hover" onClick={onClose}>
          {tp('close')}
        </Button>
        <Link to={`${ROUTES.admin}/${ROUTES.employees}/${employeeId}`}>
          <Button type="primary" className="btn-primary">
            {tp('openProfile')} →
          </Button>
        </Link>
      </div>
    </Modal>
  );
}
